import { ExternalLink, Menu, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { Button } from '@/components/ui/button'
import { getLenis } from '@/hooks/use-lenis'
import { cn } from '@/lib/cn'

function handleAnchorClick(e: React.MouseEvent<HTMLAnchorElement>, href: string) {
  const lenis = getLenis()
  if (!lenis) return
  e.preventDefault()
  lenis.scrollTo(href, { duration: 1.2 })
}

export function LandingHeader() {
  const [scrolled, setScrolled] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const sentinelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) return

    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0]
        if (first) setScrolled(!first.isIntersecting)
      },
      { threshold: 0 },
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [])

  const navLinks = [
    { label: 'Como funciona', href: '#como-funciona' },
    { label: 'Autoridade', href: '#autoridade' },
    { label: 'Pacotes', href: '#pacotes' },
    { label: 'Contato', href: '#contato' },
  ]

  return (
    <>
      <div ref={sentinelRef} className="absolute top-0 h-1 w-full" aria-hidden />
      <header
        className={cn(
          'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
          scrolled
            ? 'bg-ink-strong shadow-2xl py-3'
            : 'bg-ink-strong py-5',
        )}
      >
        <div className="mx-auto max-w-[1140px] px-4 md:px-6 lg:px-8 flex items-center justify-between">
          <a href="#" className="flex items-center gap-3 group" aria-label="Douglas Marangoni — início">
            <img
              src="/brand/logo-douglas.webp"
              alt="Douglas Marangoni"
              width={679}
              height={475}
              decoding="async"
              className="h-10 md:h-11 w-auto object-contain"
            />
            <div className="hidden sm:flex flex-col leading-none">
              <span className="font-bold text-[11px] uppercase tracking-widest text-white">
                DOUGLAS
              </span>
              <span className="font-medium text-[9px] uppercase tracking-[0.2em] text-white/50">
                MARANGONI
              </span>
            </div>
          </a>

          {/* Nav desktop */}
          <nav className="hidden md:flex items-center gap-6" aria-label="Navegação principal">
            {navLinks.map((link, i) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleAnchorClick(e, link.href)}
                className={cn(
                  'text-sm font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded',
                  i === 0
                    ? 'text-white border-b-2 border-gold pb-0.5'
                    : 'text-white/70 hover:text-white link-underline',
                )}
              >
                {link.label}
              </a>
            ))}
          </nav>

          {/* CTA desktop */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://douglasmarangoni.com.br/"
              target="_blank"
              rel="noopener noreferrer"
              className="link-underline inline-flex items-center gap-1.5 text-xs font-semibold tracking-[0.1em] uppercase px-4 py-2 rounded-md border border-gold/50 text-gold hover:bg-gold/10 hover:border-gold transition-colors duration-200"
            >
              Site Oficial
              <ExternalLink size={12} strokeWidth={2.2} />
            </a>
            <Button
              asChild
              className="bg-primary hover:bg-brand-hover text-white text-xs font-semibold tracking-[0.1em] uppercase px-5 py-2 h-auto rounded-md transition-all duration-200 shadow-sm hover:shadow-[0_4px_16px_rgba(0,158,240,0.45)] hover:scale-[1.02] active:scale-[0.98]"
            >
              <a
                href="#pacotes"
                onClick={(e) => handleAnchorClick(e, '#pacotes')}
              >
                Quero meu diagnóstico
              </a>
            </Button>
          </div>

          {/* Hambúrguer mobile */}
          <button
            className="md:hidden p-2 rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring hover:bg-white/10 text-white cursor-pointer"
            aria-label={mobileOpen ? 'Fechar menu' : 'Abrir menu'}
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((v) => !v)}
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {/* Menu mobile */}
        {mobileOpen && (
          <div className="md:hidden border-t border-white/10 bg-ink-strong px-4 pt-4 pb-5 flex flex-col gap-4">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-sm font-medium text-white/70 hover:text-white transition-colors link-underline"
                onClick={(e) => {
                  setMobileOpen(false)
                  handleAnchorClick(e, link.href)
                }}
              >
                {link.label}
              </a>
            ))}
            <a
              href="https://douglasmarangoni.com.br/"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setMobileOpen(false)}
              className="inline-flex items-center justify-center gap-1.5 text-xs font-semibold tracking-[0.1em] uppercase px-4 py-3 rounded-md border border-gold/50 text-gold hover:bg-gold/10 transition-colors"
            >
              Site Oficial
              <ExternalLink size={12} strokeWidth={2.2} />
            </a>
            <Button
              asChild
              className="bg-primary hover:bg-brand-hover text-white text-xs font-semibold tracking-[0.1em] uppercase h-auto py-3 rounded-md w-full hover:scale-[1.02] active:scale-[0.98] transition-transform duration-200"
            >
              <a
                href="#pacotes"
                onClick={(e) => {
                  setMobileOpen(false)
                  handleAnchorClick(e, '#pacotes')
                }}
              >
                Quero meu diagnóstico
              </a>
            </Button>
          </div>
        )}
      </header>
    </>
  )
}
